import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import { Box, ButtonBase, Tooltip, Typography } from "@mui/material";
import {
  PRESENCE_STATUS_LABELS,
  type ChatContact,
  type PresenceStatus,
} from "@xop/dashboard-shared";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useCanSeeSection } from "../auth/permissions.js";
import { useChatOverview } from "../lib/chat/hooks.js";
import { useRealtime } from "../lib/notifications/RealtimeProvider.js";
import { type PresenceState } from "./PresenceDot.js";
import { UserAvatar } from "./UserAvatar.js";

/** How many heads the rail shows before collapsing the rest into "+N". */
const MAX_HEADS = 30;

/**
 * The global right-edge presence rail — a vertical strip present on every
 * authed page. It lists **every teammate** (online first, then offline) as a
 * head with a live presence dot + unread badge; clicking one jumps straight
 * into that 1:1 chat. Hidden below `md` (the rail would crowd a phone; the
 * in-page roster covers mobile).
 *
 * Reads the same `chat/overview` query + realtime presence the Chat page does
 * — all shared, deduped caches — so it costs no extra fetch and updates live.
 */
export function PresenceRail(): JSX.Element | null {
  const visible = useCanSeeSection("chat");
  const overview = useChatOverview(visible);
  const { online } = useRealtime();
  const navigate = useNavigate();

  const presence = useMemo(() => {
    const map = new Map<number, PresenceStatus>();
    for (const e of online) map.set(e.user_id, e.status);
    return map;
  }, [online]);

  // Every teammate (incl. offline), ranked: unread first, then online, then
  // alphabetical. The offline ones still show — just lower + with a grey dot.
  const ranked = useMemo(() => {
    const contacts = overview.data?.contacts ?? [];
    return contacts
      .map((c) => ({ contact: c, state: (presence.get(c.user_id) ?? "offline") as PresenceState }))
      .sort((a, b) => {
        if (b.contact.unread !== a.contact.unread) return b.contact.unread - a.contact.unread;
        const aOn = a.state !== "offline" ? 1 : 0;
        const bOn = b.state !== "offline" ? 1 : 0;
        if (aOn !== bOn) return bOn - aOn;
        return a.contact.display_name.localeCompare(b.contact.display_name);
      });
  }, [overview.data, presence]);

  if (!visible) return null;

  const shown = ranked.slice(0, MAX_HEADS);
  const overflow = ranked.length - shown.length;

  return (
    <Box
      data-testid="presence-rail"
      aria-label="Teammates"
      sx={{
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        alignItems: "center",
        gap: 1.25,
        width: 62,
        flexShrink: 0,
        position: "sticky",
        top: 56,
        alignSelf: "flex-start",
        height: "calc(100dvh - 56px)",
        py: 1.5,
        borderLeft: 1,
        borderColor: "divider",
        bgcolor: (t) => t.palette.background.paper,
        overflowY: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <Tooltip title="Open chat" placement="left">
        <ButtonBase
          onClick={() => navigate("/chat")}
          aria-label="Open chat"
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            color: "text.secondary",
            "&:hover": { bgcolor: "action.hover", color: "primary.main" },
          }}
        >
          <ChatRoundedIcon sx={{ fontSize: 22 }} />
        </ButtonBase>
      </Tooltip>

      <Box
        aria-hidden
        sx={{ width: 22, height: "1px", bgcolor: "divider", my: 0.25, flexShrink: 0 }}
      />

      {shown.map(({ contact, state }) => (
        <RailHead
          key={contact.user_id}
          contact={contact}
          state={state}
          onClick={() => navigate(`/chat?dm=${contact.user_id}`)}
        />
      ))}

      {overflow > 0 && (
        <Tooltip title={`${overflow} more`} placement="left">
          <ButtonBase
            onClick={() => navigate("/chat")}
            aria-label={`${overflow} more teammates`}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "surfaceSoft",
              border: 1,
              borderColor: "divider",
              fontSize: 12,
              fontWeight: 700,
              color: "text.secondary",
              flexShrink: 0,
            }}
          >
            +{overflow}
          </ButtonBase>
        </Tooltip>
      )}

      {shown.length === 0 && (
        <Typography sx={{ fontSize: 10.5, color: "text.disabled", textAlign: "center", px: 0.5, mt: 1 }}>
          No teammates
        </Typography>
      )}
    </Box>
  );
}

function RailHead({
  contact,
  state,
  onClick,
}: {
  contact: ChatContact;
  state: PresenceState;
  onClick: () => void;
}): JSX.Element {
  const statusLabel = state === "offline" ? "Offline" : PRESENCE_STATUS_LABELS[state as PresenceStatus];
  return (
    <Tooltip title={`${contact.display_name} · ${statusLabel}`} placement="left">
      <ButtonBase
        onClick={onClick}
        aria-label={`Chat with ${contact.display_name}`}
        sx={{ borderRadius: "50%", position: "relative", flexShrink: 0 }}
      >
        <UserAvatar name={contact.display_name} userId={contact.user_id} size={44} presence={state} />
        {contact.unread > 0 && (
          <Box
            data-testid="rail-unread"
            sx={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              px: 0.5,
              borderRadius: 9999,
              bgcolor: "error.main",
              color: "error.contrastText",
              fontSize: 10.5,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: 2,
              borderColor: "background.paper",
            }}
          >
            {contact.unread > 9 ? "9+" : contact.unread}
          </Box>
        )}
      </ButtonBase>
    </Tooltip>
  );
}
